import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { StatusBadge } from "../components/StatusBadge";
import { toast } from "sonner";
import { pesos } from "../lib/format";

const ESTADOS = ["Bueno", "Regular", "Malo"];
const RANK = { Bueno: 0, Regular: 1, Malo: 2 };
const hoyISO = () => new Date().toISOString().slice(0, 10);
const PASOS = ["Aviso", "Fecha de salida", "Inspección", "Devolución"];

export default function RescisionWizard({ open, onOpenChange, contrato, detalle, saldoActual, onComplete }) {
  const [step, setStep] = useState(0);
  const [aviso, setAviso] = useState({ quienAviso: "Inquilino", fecha: hoyISO() });
  const [fechaPrevista, setFechaPrevista] = useState("");
  const [insp, setInsp] = useState(() =>
    detalle.checkin.inventario.flatMap((inv) =>
      inv.items.map((it) => ({ ambiente: inv.ambiente, item: it.item, entrada: it.estado, salida: it.estado, accion: "Sin cambios", costo: 0 }))
    )
  );

  const setSalida = (idx, salida) =>
    setInsp((prev) =>
      prev.map((x, i) => {
        if (i !== idx) return x;
        const peor = RANK[salida] > RANK[x.entrada];
        return { ...x, salida, accion: peor ? "Reparar" : "Sin cambios", costo: peor ? x.costo || 60000 : 0 };
      })
    );
  const setCosto = (idx, costo) =>
    setInsp((prev) => prev.map((x, i) => (i === idx ? { ...x, costo: Math.max(0, Number(costo) || 0) } : x)));

  const costoArreglos = insp.reduce((s, x) => s + (Number(x.costo) || 0), 0);
  const deuda = saldoActual > 0 ? saldoActual : 0;
  const montoBase = detalle.deposito.monto;
  const descuentos = [
    ...(costoArreglos > 0 ? [{ concepto: "Arreglos a cargo del inquilino", monto: costoArreglos }] : []),
    ...(deuda > 0 ? [{ concepto: "Deuda pendiente", monto: deuda }] : []),
  ];
  const saldo = Math.max(0, montoBase - costoArreglos - deuda);

  const siguiente = () => {
    if (step === 1 && !fechaPrevista) {
      toast.error("Elegí la fecha prevista de salida.");
      return;
    }
    setStep((s) => Math.min(s + 1, 3));
  };
  const anterior = () => setStep((s) => Math.max(s - 1, 0));

  const finalizar = () => {
    onComplete({
      aviso: { fecha: new Date(aviso.fecha).toISOString(), quienAviso: aviso.quienAviso },
      fechaPrevista: new Date(fechaPrevista).toISOString(),
      inspeccion: insp,
      devolucion: { montoBase, descuentos, saldo },
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="rescision-wizard">
        <DialogHeader>
          <DialogTitle>Rescisión y check-out — {contrato.direccion}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          {PASOS.map((p, i) => (
            <div key={p} className="flex items-center gap-2">
              <span className={`flex h-6 w-6 items-center justify-center rounded-md text-xs font-semibold ${i <= step ? "bg-brand text-white" : "bg-slate-100 text-slate-400"}`}>{i + 1}</span>
              <span className={`text-xs ${i === step ? "font-semibold text-slate-900" : "text-slate-400"}`}>{p}</span>
              {i < PASOS.length - 1 && <span className="hidden sm:block w-4 h-px bg-slate-200" />}
            </div>
          ))}
        </div>

        <div className="mt-2 min-h-[220px]">
          {step === 0 && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">¿Quién avisó la rescisión?</Label>
                <Select value={aviso.quienAviso} onValueChange={(v) => setAviso({ ...aviso, quienAviso: v })}>
                  <SelectTrigger className="rounded-md mt-1" data-testid="rescision-quien"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Inquilino">Inquilino</SelectItem>
                    <SelectItem value="Propietario">Propietario</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Fecha del aviso</Label>
                <Input type="date" value={aviso.fecha} onChange={(e) => setAviso({ ...aviso, fecha: e.target.value })} className="rounded-md mt-1" />
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Fecha prevista de salida</Label>
                <Input type="date" value={fechaPrevista} onChange={(e) => setFechaPrevista(e.target.value)} className="rounded-md mt-1" data-testid="rescision-fecha-salida" />
              </div>
              <p className="text-xs text-slate-400">El inquilino tiene que entregar la unidad en condiciones similares a las de la entrada. En el próximo paso comparás el estado ambiente por ambiente.</p>
            </div>
          )}
          {step === 2 && (
            <div className="overflow-x-auto max-h-64 overflow-y-auto">
              <Table data-testid="rescision-inspeccion">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Ítem</TableHead>
                    <TableHead>Entrada</TableHead>
                    <TableHead>Salida</TableHead>
                    <TableHead className="text-right">Costo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {insp.map((x, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs text-slate-600">{x.ambiente} · {x.item}</TableCell>
                      <TableCell><StatusBadge estado={x.entrada} /></TableCell>
                      <TableCell>
                        <Select value={x.salida} onValueChange={(v) => setSalida(i, v)}>
                          <SelectTrigger className="h-8 w-24 rounded-md text-xs" data-testid={`rescision-salida-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <Input type="number" value={x.costo} onChange={(e) => setCosto(i, e.target.value)} className="h-8 w-24 ml-auto rounded-md text-right text-xs" disabled={RANK[x.salida] <= RANK[x.entrada]} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Depósito en garantía</span><span className="text-slate-800">{pesos(montoBase)}</span></div>
              {descuentos.length === 0 && <p className="text-xs text-emerald-700">Sin descuentos: se devuelve el depósito completo.</p>}
              {descuentos.map((d, i) => (
                <div key={i} className="flex justify-between"><span className="text-slate-500">{d.concepto}</span><span className="text-red-700">-{pesos(d.monto)}</span></div>
              ))}
              <div className="flex justify-between pt-2 mt-2 border-t border-slate-100">
                <span className="font-semibold text-slate-900">Saldo a devolver al inquilino</span>
                <span className="font-bold text-emerald-700" data-testid="rescision-saldo">{pesos(saldo)}</span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button variant="outline" className="rounded-md" onClick={step === 0 ? () => onOpenChange(false) : anterior}>
            {step === 0 ? "Cancelar" : "Atrás"}
          </Button>
          {step < 3 ? (
            <Button className="btn-brand rounded-md" data-testid="rescision-next" onClick={siguiente}>Siguiente</Button>
          ) : (
            <Button className="btn-brand rounded-md" data-testid="rescision-finish" onClick={finalizar}>Confirmar salida</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
